/**
 * Custom Cart Drawer
 *
 * Server-rendered section HTML is the source of truth. Internal mutations
 * (qty changes, removals, gift picker adds) POST with `sections: this.sectionId`;
 * the response includes server-rendered HTML which we patch into the live drawer
 * via sub-section swaps (with the meter kept out of the swap so CSS transitions
 * still fire on the fill widths).
 *
 * Threshold-drop gift auto-removal is atomic: predictGiftRemoval inspects the
 * DOM before the mutation to decide whether to use /cart/update.js (which lets
 * us change the line qty AND remove the gift in a single request). No multi-step
 * async chains, no races with subsequent user actions.
 *
 * Only the qty input value updates optimistically. Subtotals, line prices,
 * picker state, progress bar, milestones — all wait for the server response.
 * The 300ms debounce + atomic single-request mutations hide the latency.
 */

class CustomCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.isAddingToCart = false;
    this.sectionId = null;
    this.giftHandles = [];
    this.freeGiftItemThreshold = 0;
    this._queuedUpdates = new Map();
    this._debounceTimers = new Map();
    this._selfTriggeredCount = 0;
    this._lastRenderedHTML = null;
  }

  connectedCallback() {
    this.sectionId = this.extractSectionId();
    this.backdrop = this.querySelector('[data-ccd-backdrop]');
    this.drawer = this.querySelector('[data-ccd-drawer]');

    this.giftHandles = (this.dataset.giftHandles || '')
      .split(',')
      .map((h) => h.trim())
      .filter(Boolean);
    this.freeGiftItemThreshold = parseInt(this.dataset.freeGiftThreshold, 10) || 0;

    this.bindEvents();
    this.bindThemeEvents();
    this.interceptCartIconClick();
  }

  extractSectionId() {
    const wrapper = this.closest('[id^="shopify-section-"]');
    if (wrapper) return wrapper.id.replace('shopify-section-', '');
    return this.dataset.sectionId;
  }

  /* ====================================================================
     Event Binding
     ==================================================================== */

  bindEvents() {
    this.backdrop?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    this.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('[data-ccd-close]');
      if (closeBtn) { e.preventDefault(); this.close(); return; }

      const minusBtn = e.target.closest('[data-ccd-qty-minus]');
      if (minusBtn) { this.handleQuantityChange(minusBtn.dataset.key, -1); return; }

      const plusBtn = e.target.closest('[data-ccd-qty-plus]');
      if (plusBtn) { this.handleQuantityChange(plusBtn.dataset.key, 1); return; }

      const removeBtn = e.target.closest('[data-ccd-remove]');
      if (removeBtn) { this.handleRemoveItem(removeBtn.dataset.key); return; }

      const giftBtn = e.target.closest('[data-ccd-add-gift]');
      if (giftBtn) { this.handleAddToCart(giftBtn.dataset.variantId, true, giftBtn); return; }

      const upsellBtn = e.target.closest('[data-ccd-add-upsell]');
      if (upsellBtn) { this.handleAddToCart(upsellBtn.dataset.variantId, false, upsellBtn); return; }

      const emptyAddBtn = e.target.closest('[data-ccd-add-empty]');
      if (emptyAddBtn) { this.handleAddToCart(emptyAddBtn.dataset.variantId, false, emptyAddBtn); return; }

      const bundleToggle = e.target.closest('[data-ccd-bundle-toggle]');
      if (bundleToggle) { this.toggleBundle(bundleToggle); return; }
    });

    this.addEventListener('change', (e) => {
      const qtyInput = e.target.closest('[data-ccd-qty-input]');
      if (qtyInput) {
        const qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
        qtyInput.value = qty;
        this.setItemLoading(qtyInput.dataset.key, true);
        this.debouncedUpdateItem(qtyInput.dataset.key, qty);
        return;
      }

      const variantSelect = e.target.closest('[data-ccd-upsell-variant]');
      if (variantSelect) {
        const card = variantSelect.closest('.ccd-upsells__slide');
        const addBtn = card?.querySelector('[data-ccd-add-upsell]');
        if (addBtn) addBtn.dataset.variantId = variantSelect.value;
        return;
      }

      const emptyVariant = e.target.closest('[data-ccd-empty-variant]');
      if (emptyVariant) {
        const product = emptyVariant.closest('.ccd-empty__product');
        const addBtn = product?.querySelector('[data-ccd-add-empty]');
        if (addBtn) addBtn.dataset.variantId = emptyVariant.value;
      }
    });
  }

  /* ====================================================================
     Theme Event Integration
     ==================================================================== */

  bindThemeEvents() {
    document.addEventListener('cart:prepare-bundled-sections', (e) => {
      if (this.sectionId) e.detail.sections.add(this.sectionId);
    });

    document.documentElement.addEventListener('cart:change', (e) => {
      const detail = e.detail;
      if (!detail) return;

      if (this._selfTriggeredCount > 0) {
        this._selfTriggeredCount--;
        return;
      }

      // External mutation (e.g. product-page add): apply server-rendered HTML
      const sectionHtml = detail.cart?.sections?.[this.sectionId];
      if (sectionHtml) {
        this.replaceDrawerContent(sectionHtml);
      } else {
        this.refreshSection();
      }

      if (detail.baseEvent === 'variant:add' && !this.isOpen) {
        requestAnimationFrame(() => this.open());
      }
    });

    document.documentElement.addEventListener('cart:refresh', () => {
      this.refreshSection();
    });

    window.addEventListener('pageshow', (e) => {
      if (e.persisted) this.refreshSection();
    });
  }

  /* ====================================================================
     Cart Icon Click Interception
     ==================================================================== */

  interceptCartIconClick() {
    document.addEventListener('click', (e) => {
      const cartLink = e.target.closest('a[href="/cart"], a[href$="/cart"]');
      if (!cartLink) return;
      if (window.location.pathname === '/cart') return;

      const isHeaderLink = cartLink.closest('.header, header, [class*="header"]');
      if (isHeaderLink) {
        e.preventDefault();
        e.stopPropagation();
        this.open();
      }
    }, true);
  }

  /* ====================================================================
     Open / Close
     ==================================================================== */

  open() {
    this.isOpen = true;
    this.backdrop?.classList.add('is-visible');
    this.drawer?.classList.add('is-open');
    document.body.classList.add('ccd-open');
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));
  }

  close() {
    this.isOpen = false;
    this.backdrop?.classList.remove('is-visible');
    this.drawer?.classList.remove('is-open');
    document.body.classList.remove('ccd-open');
    document.dispatchEvent(new CustomEvent('cart-drawer:close'));
  }

  /* ====================================================================
     Bundle Toggle
     ==================================================================== */

  toggleBundle(button) {
    const bundleItems = button.parentElement?.querySelector('[data-ccd-bundle-items]');
    if (!bundleItems) return;
    const isExpanded = button.classList.toggle('is-expanded');
    bundleItems.style.display = isExpanded ? 'block' : 'none';
    const itemCount = bundleItems.querySelectorAll('.ccd-bundle__component').length;
    const svg = button.querySelector('svg').outerHTML;
    button.innerHTML = (isExpanded ? `Hide ${itemCount} items` : `Show ${itemCount} items`) + svg;
  }

  /* ====================================================================
     Loading Indicators
     ==================================================================== */

  setItemLoading(key, loading) {
    const item = this.querySelector(`[data-line-key="${key}"]`);
    if (!item) return;
    item.classList.toggle('ccd-item--loading', loading);
  }

  setDrawerLoading(loading) {
    const body = this.querySelector('[data-ccd-body]');
    if (!body) return;
    body.classList.toggle('ccd-body--loading', loading);
  }

  /* ====================================================================
     Gift detection + threshold-drop prediction
     ==================================================================== */

  isGiftHandle(handle) {
    return !!handle && this.giftHandles.includes(handle);
  }

  /**
   * From current DOM, predict whether changing line `key` to `newQty` will drop
   * the non-gift item count below the gift threshold while a gift line is in
   * the cart. Returns the gift line key to remove, or null.
   *
   * Used to decide between /cart/change.js (normal qty change) and
   * /cart/update.js (atomic qty change + gift removal).
   */
  predictGiftRemoval(key, newQty) {
    if (this.freeGiftItemThreshold <= 0) return null;
    if (!this.giftHandles.length) return null;

    const targetLine = this.querySelector(`[data-line-key="${key}"]`);
    if (!targetLine) return null;
    // Changing the gift line itself doesn't affect non-gift count
    if (this.isGiftHandle(targetLine.dataset.productHandle)) return null;

    let newNonGiftCount = newQty;  // target line's contribution after the change
    let giftLineKey = null;

    this.querySelectorAll('[data-line-key]').forEach((el) => {
      if (el.dataset.lineKey === key) return;          // already counted as newQty
      if (el.dataset.removing === 'true') return;       // mid-animation, treat as gone

      const handle = el.dataset.productHandle;
      if (this.isGiftHandle(handle)) {
        if (!giftLineKey) giftLineKey = el.dataset.lineKey;
      } else {
        const input = el.querySelector('[data-ccd-qty-input]');
        const qty = input ? parseInt(input.value, 10) || 0 : 1;
        newNonGiftCount += qty;
      }
    });

    if (!giftLineKey) return null;
    if (newNonGiftCount < this.freeGiftItemThreshold) return giftLineKey;
    return null;
  }

  /* ====================================================================
     Cart mutations
     ==================================================================== */

  handleQuantityChange(key, delta) {
    const input = this.querySelector(`[data-ccd-qty-input][data-key="${key}"]`);
    if (!input) return;

    const currentQty = parseInt(input.value, 10) || 1;
    const newQty = Math.max(0, currentQty + delta);

    if (newQty === 0) {
      this.handleRemoveItem(key);
      return;
    }

    // Only optimistic update: the qty input value. Everything else waits for
    // server-rendered HTML so the BXGY-aware pricing stays authoritative.
    input.value = newQty;
    this.setItemLoading(key, true);
    this.debouncedUpdateItem(key, newQty);
  }

  debouncedUpdateItem(key, quantity) {
    this._queuedUpdates.set(key, quantity);
    const existing = this._debounceTimers.get(key);

    // First click fires immediately — no delay for single taps
    if (!existing) {
      this._debounceTimers.set(key, setTimeout(() => {
        this._debounceTimers.delete(key);
      }, 300));
      this._queuedUpdates.delete(key);
      this.updateCartItem(key, quantity);
      return;
    }

    // Rapid follow-up clicks: reset timer, batch into one request
    clearTimeout(existing);
    this._debounceTimers.set(key, setTimeout(() => {
      this._debounceTimers.delete(key);
      const qty = this._queuedUpdates.get(key);
      this._queuedUpdates.delete(key);
      if (qty !== undefined) this.updateCartItem(key, qty);
    }, 300));
  }

  async handleRemoveItem(key) {
    const timer = this._debounceTimers.get(key);
    if (timer) {
      clearTimeout(timer);
      this._debounceTimers.delete(key);
      this._queuedUpdates.delete(key);
    }

    const item = this.querySelector(`[data-line-key="${key}"]`);
    if (item) {
      item.dataset.removing = 'true';
      item.style.transition = 'opacity 0.15s ease, max-height 0.2s ease';
      item.style.opacity = '0';
      item.style.maxHeight = item.offsetHeight + 'px';
      requestAnimationFrame(() => {
        item.style.maxHeight = '0';
        item.style.overflow = 'hidden';
      });
    }

    this.setDrawerLoading(true);
    this.updateCartItem(key, 0);
  }

  /**
   * Submit a line item quantity change. If the change would drop the cart below
   * the gift threshold while a gift is in the cart, both changes are batched
   * into a single atomic /cart/update.js request so the server sees the final
   * state in one shot — no chained async, no race with subsequent user actions.
   */
  async updateCartItem(key, quantity) {
    const giftKeyToRemove = this.predictGiftRemoval(key, quantity);

    let url;
    let body;
    if (giftKeyToRemove) {
      url = '/cart/update.js';
      body = JSON.stringify({
        updates: { [key]: quantity, [giftKeyToRemove]: 0 },
        sections: this.sectionId,
      });
    } else {
      url = '/cart/change.js';
      body = JSON.stringify({
        id: key,
        quantity,
        sections: this.sectionId,
      });
    }

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body,
      });
      const data = await res.json();

      // Empty cart transition needs the empty-state markup
      if (data.item_count === 0) {
        await this.refreshSection();
        this.dispatchCartChange('line-item:change', data);
        return;
      }

      const sectionHtml = data.sections?.[this.sectionId];
      if (sectionHtml) {
        this.replaceDrawerContent(sectionHtml);
      } else {
        await this.refreshSection();
      }

      this.dispatchCartChange('line-item:change', data);
    } catch (err) {
      console.error('Cart update error:', err);
      await this.refreshSection();
    } finally {
      this.setItemLoading(key, false);
      this.setDrawerLoading(false);
    }
  }

  async handleAddToCart(variantId, isGift, sourceBtn) {
    if (this.isAddingToCart) return;
    this.isAddingToCart = true;

    if (sourceBtn) {
      sourceBtn.disabled = true;
      sourceBtn.dataset.originalText = sourceBtn.textContent;
      sourceBtn.textContent = 'Adding...';
    }

    // Safety timeout: reset button + refresh if the operation hangs
    const safetyTimer = setTimeout(() => {
      if (this.isAddingToCart) {
        this.isAddingToCart = false;
        if (sourceBtn) {
          sourceBtn.textContent = sourceBtn.dataset.originalText || 'Add';
          sourceBtn.disabled = false;
        }
        this.refreshSection();
      }
    }, 10000);

    try {
      // Gift picker swap: remove any existing gift first so the customer ends
      // up with exactly one gift line. We don't apply this intermediate cart
      // state — the /cart/add response below has the authoritative HTML.
      if (isGift) {
        await this.removeExistingGift();
      }

      const body = {
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        sections: this.sectionId,
      };

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body),
      });
      const addData = await addRes.json();

      const sectionHtml = addData.sections?.[this.sectionId];
      if (sectionHtml) {
        this.replaceDrawerContent(sectionHtml);
      } else {
        await this.refreshSection();
      }

      this.dispatchCartChange('variant:add', addData);
    } catch (err) {
      console.error('Add to cart error:', err);
      await this.refreshSection();
    } finally {
      clearTimeout(safetyTimer);
      if (sourceBtn) {
        sourceBtn.textContent = sourceBtn.dataset.originalText || 'Add';
        sourceBtn.disabled = false;
      }
      this.isAddingToCart = false;
    }
  }

  /**
   * Picker-swap helper: find any existing gift line in the DOM and remove it
   * from the cart. Only called from handleAddToCart(isGift=true) so the
   * subsequent /cart/add response is what we apply — no intermediate state.
   */
  async removeExistingGift() {
    const giftEl = Array.from(this.querySelectorAll('[data-line-key]'))
      .find((el) => this.isGiftHandle(el.dataset.productHandle));
    if (!giftEl) return;
    const giftKey = giftEl.dataset.lineKey;
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: giftKey, quantity: 0 }),
    });
  }

  dispatchCartChange(baseEvent, cart) {
    this._selfTriggeredCount++;
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent, cart },
    }));
  }

  /* ====================================================================
     Section rendering — apply server HTML
     ==================================================================== */

  replaceDrawerContent(html) {
    if (html === this._lastRenderedHTML) return;
    this._lastRenderedHTML = html;

    const temp = document.createElement('div');
    temp.innerHTML = html;

    const newComponent = temp.querySelector('custom-cart-drawer');
    if (!newComponent) return;

    // Sync host attributes — CSS reads these for picker visibility/locked state
    this.toggleAttribute('data-gift-eligible', newComponent.hasAttribute('data-gift-eligible'));
    this.toggleAttribute('data-has-gift', newComponent.hasAttribute('data-has-gift'));

    const newDrawer = newComponent.querySelector('.ccd-drawer');
    if (!newDrawer) return;

    // Empty <-> non-empty transition needs a full structural swap
    const currentHasItems = !!this.querySelector('[data-ccd-body]');
    const newHasItems = !!newDrawer.querySelector('[data-ccd-body]');
    if (currentHasItems !== newHasItems) {
      if (this.isOpen) newDrawer.classList.add('is-open');
      const currentDrawer = this.querySelector('.ccd-drawer');
      if (currentDrawer) currentDrawer.replaceWith(newDrawer);
      this.drawer = newDrawer;
      return;
    }

    const body = this.querySelector('[data-ccd-body]');
    const scrollTop = body ? body.scrollTop : 0;

    // Meter: stays mounted, only widths/classes/message swap so CSS transitions fire
    this.transitionProgressBar(newDrawer);

    // Line items: surgical diff with enter/exit animations
    this.patchItems(newDrawer);

    // Footer: patch text nodes
    this.patchFooter(newDrawer);

    // Light sub-sections: full innerHTML swap (only if content differs)
    const lightSections = ['[data-ccd-empty]', '[data-ccd-gifts]'];
    for (const sel of lightSections) {
      const current = this.querySelector(sel);
      const incoming = newDrawer.querySelector(sel);
      if (current && incoming) {
        if (current.outerHTML !== incoming.outerHTML) current.replaceWith(incoming);
      } else if (!current && incoming) {
        const bodyEl = this.querySelector('[data-ccd-body]');
        if (bodyEl) {
          const upsells = bodyEl.querySelector('[data-ccd-upsells]');
          if (upsells) bodyEl.insertBefore(incoming, upsells);
          else bodyEl.appendChild(incoming);
        }
      } else if (current && !incoming) {
        current.remove();
      }
    }

    // Header count
    const newCount = newDrawer.querySelector('[data-ccd-count]');
    const currentCount = this.querySelector('[data-ccd-count]');
    if (newCount && currentCount) currentCount.textContent = newCount.textContent;

    // Upsells (only re-render if the recommended product changed)
    this.patchUpsells(newDrawer);

    if (body) body.scrollTop = scrollTop;
  }

  patchItems(newDrawer) {
    const currentList = this.querySelector('[data-ccd-items]');
    const incomingList = newDrawer.querySelector('[data-ccd-items]');

    if (!currentList && incomingList) {
      const parent = this.drawer.querySelector('.ccd-body') || this.drawer;
      parent.appendChild(incomingList);
      return;
    }
    if (currentList && !incomingList) { currentList.remove(); return; }
    if (!currentList || !incomingList) return;

    const currentMap = new Map();
    currentList.querySelectorAll('[data-line-key]').forEach((el) => {
      currentMap.set(el.dataset.lineKey, el);
    });

    const incomingMap = new Map();
    incomingList.querySelectorAll('[data-line-key]').forEach((el) => {
      incomingMap.set(el.dataset.lineKey, el);
    });

    // Removed items: animate out
    currentMap.forEach((item, key) => {
      if (incomingMap.has(key)) return;
      item.dataset.removing = 'true';
      item.style.transition = 'opacity 0.15s ease, max-height 0.2s ease';
      item.style.opacity = '0';
      item.style.maxHeight = item.offsetHeight + 'px';
      requestAnimationFrame(() => {
        item.style.maxHeight = '0';
        item.style.overflow = 'hidden';
      });
      setTimeout(() => item.remove(), 200);
    });

    // Existing items: replace with server version (preserve bundle toggle state).
    // Skip lines with a pending debounce — the user's typed qty is more recent.
    incomingMap.forEach((incoming, key) => {
      const current = currentMap.get(key);
      if (!current) return;
      if (this._debounceTimers.has(key)) return;

      const bundleExpanded = current.querySelector('[data-ccd-bundle-toggle]')?.getAttribute('aria-expanded') === 'true';
      current.replaceWith(incoming);
      if (bundleExpanded) {
        const newToggle = incoming.querySelector('[data-ccd-bundle-toggle]');
        if (newToggle) {
          newToggle.setAttribute('aria-expanded', 'true');
          const bundleContent = incoming.querySelector('[data-ccd-bundle-content]');
          if (bundleContent) bundleContent.style.display = '';
        }
      }
    });

    // New items: enter animation
    incomingMap.forEach((incoming, key) => {
      if (currentMap.has(key)) return;
      incoming.style.opacity = '0';
      incoming.style.maxHeight = '0';
      incoming.style.overflow = 'hidden';
      incoming.style.transition = 'opacity 0.2s ease 0.05s, max-height 0.25s ease';
      currentList.appendChild(incoming);
      requestAnimationFrame(() => {
        incoming.style.maxHeight = '200px';
        incoming.style.opacity = '1';
        setTimeout(() => {
          incoming.style.removeProperty('max-height');
          incoming.style.removeProperty('overflow');
          incoming.style.removeProperty('transition');
          incoming.style.removeProperty('opacity');
        }, 300);
      });
    });
  }

  patchFooter(newDrawer) {
    const currentFooter = this.querySelector('[data-ccd-footer]');
    const incomingFooter = newDrawer.querySelector('[data-ccd-footer]');

    if (!currentFooter && incomingFooter) { this.drawer.appendChild(incomingFooter); return; }
    if (currentFooter && !incomingFooter) { currentFooter.remove(); return; }
    if (!currentFooter || !incomingFooter) return;

    const curSub = currentFooter.querySelector('[data-ccd-subtotal]');
    const newSub = incomingFooter.querySelector('[data-ccd-subtotal]');
    if (curSub && newSub) curSub.textContent = newSub.textContent;

    const curCheckout = currentFooter.querySelector('[data-ccd-checkout-total]');
    const newCheckout = incomingFooter.querySelector('[data-ccd-checkout-total]');
    if (curCheckout && newCheckout) curCheckout.textContent = newCheckout.textContent;

    const curDiscounts = currentFooter.querySelector('[data-ccd-discounts]');
    const newDiscounts = incomingFooter.querySelector('[data-ccd-discounts]');
    if (curDiscounts && newDiscounts) {
      if (curDiscounts.innerHTML !== newDiscounts.innerHTML) {
        curDiscounts.innerHTML = newDiscounts.innerHTML;
      }
    } else if (!curDiscounts && newDiscounts) {
      currentFooter.insertBefore(newDiscounts, currentFooter.firstChild);
    } else if (curDiscounts && !newDiscounts) {
      curDiscounts.remove();
    }
  }

  patchUpsells(newDrawer) {
    const current = this.querySelector('[data-ccd-upsells]');
    const incoming = newDrawer.querySelector('[data-ccd-upsells]');

    if (current && incoming) {
      const currentProduct = current.querySelector('product-recommendations')?.getAttribute('product');
      const incomingProduct = incoming.querySelector('product-recommendations')?.getAttribute('product');
      if (currentProduct !== incomingProduct) current.replaceWith(incoming);
    } else if (!current && incoming) {
      const body = this.querySelector('[data-ccd-body]');
      if (body) body.appendChild(incoming);
    } else if (current && !incoming) {
      current.remove();
    }
  }

  transitionProgressBar(newDrawer) {
    const currentRewards = this.querySelector('[data-ccd-rewards]');
    const incomingRewards = newDrawer.querySelector('[data-ccd-rewards]');

    if (!currentRewards || !incomingRewards) {
      if (currentRewards && !incomingRewards) currentRewards.remove();
      if (!currentRewards && incomingRewards) {
        const body = this.querySelector('[data-ccd-body]');
        if (body) body.prepend(incomingRewards);
      }
      return;
    }

    // Update reward message text
    const currentMsg = currentRewards.querySelector('[data-ccd-rewards-msg]');
    const incomingMsg = incomingRewards.querySelector('[data-ccd-rewards-msg]');
    if (currentMsg && incomingMsg) currentMsg.innerHTML = incomingMsg.innerHTML;

    // Update inline widths so CSS transitions animate the fill
    const incomingSeg1 = incomingRewards.querySelector('[data-ccd-seg1]');
    const incomingSeg2 = incomingRewards.querySelector('[data-ccd-seg2]');
    const currentSeg1 = currentRewards.querySelector('[data-ccd-seg1]');
    const currentSeg2 = currentRewards.querySelector('[data-ccd-seg2]');
    if (currentSeg1 && incomingSeg1) currentSeg1.style.width = incomingSeg1.style.width;
    if (currentSeg2 && incomingSeg2) currentSeg2.style.width = incomingSeg2.style.width;

    // Milestone unlock animations
    this.updateMilestone(
      this.querySelector('[data-ccd-ms1]'),
      incomingRewards.querySelector('[data-ccd-ms1]')?.classList.contains('is-unlocked')
    );
    this.updateMilestone(
      this.querySelector('[data-ccd-ms2]'),
      incomingRewards.querySelector('[data-ccd-ms2]')?.classList.contains('is-unlocked')
    );
  }

  updateMilestone(el, shouldUnlock) {
    if (!el) return;
    const wasUnlocked = el.classList.contains('is-unlocked');
    if (shouldUnlock && !wasUnlocked) {
      el.classList.add('is-unlocked', 'is-just-unlocked');
      el.addEventListener('animationend', () => {
        el.classList.remove('is-just-unlocked');
      }, { once: true });
    } else if (!shouldUnlock && wasUnlocked) {
      el.classList.remove('is-unlocked');
    }
  }

  async refreshSection() {
    try {
      const res = await fetch(`/?sections=${this.sectionId}`);
      const data = await res.json();
      const html = data[this.sectionId];
      if (html) this.replaceDrawerContent(html);
    } catch (err) {
      console.error('Section refresh error:', err);
    }
  }
}

if (!customElements.get('custom-cart-drawer')) {
  customElements.define('custom-cart-drawer', CustomCartDrawer);
}
